import { useState, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { renderReactComponent, destroyReactComponent } from '@/react-bridge';
import { navigateToModuleSelected } from '@/services/navigation';
import { getNavigateRef } from '@/router/navigate-ref';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
const w = window as any;

// ── Types ─────────────────────────────────────────────────────────────

interface ModuleDef {
  key: string;
  label: string;
}

interface SelectModuleScreenProps {
  modules: ModuleDef[];
}

// ── Component ─────────────────────────────────────────────────────────

export function SelectModuleScreen({ modules }: SelectModuleScreenProps) {
  const { theme } = useTheme();
  const [isSelecting, setIsSelecting] = useState(false);

  const selectModule = useCallback((moduleKey: string) => {
    setIsSelecting(true);
    api.selectModule(
      moduleKey,
      (result: any) => {
        w.handleMdtModuleSelection(result, () => {
          w.selectModuleCallback(result);
          destroySelectModuleScreen();
          const section = (result.ModuleType || '').toUpperCase();

          // Restore last route after crash/reload on mobile apps
          const lastRoute = localStorage.getItem('LastRoute');
          localStorage.removeItem('LastRoute');
          if (lastRoute && lastRoute.toLowerCase().startsWith('/' + section.toLowerCase() + '/')) {
            const nav = getNavigateRef();
            if (nav) {
              nav(lastRoute, { replace: true });
            } else {
              navigateToModuleSelected(section);
            }
          } else {
            navigateToModuleSelected(section);
          }
        });
      },
      () => {
        setIsSelecting(false);
      }
    );
  }, []);

  const logOut = useCallback(() => {
    App.menuLogOutClick(null);
    App.showPopupNotification('LOGGING OUT', 'error');
  }, []);

  return (
    <div className={`select-module ${theme}`.trim()}>
      <div className="select-module__layout">
        <div className="select-module__header">
          <h1 className="select-module__title">Welcome!</h1>
          <h4 className="select-module__subtitle">Please select a module.</h4>
        </div>
        <div className="select-module__cards">
          {modules.map((m) => (
            <div className="select-module__card" key={m.key}>
              <div className="select-module__card-body">
                <button
                  type="button"
                  className="select-module__card-btn"
                  disabled={isSelecting}
                  onClick={() => selectModule(m.key)}
                >
                  {m.label}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="select-module__footer">
          <button
            type="button"
            className="select-module__logout-btn"
            onClick={logOut}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Imperative API (bridges legacy non-React code) ────────────────────

let selectModuleContainer: HTMLElement | null = null;

/**
 * Wrapper that computes the available modules from globals and passes
 * them as props to SelectModuleScreen.
 */
export function SelectModuleScreenWrapper() {
  const buttonsOrder = ['cad', 'mdt', 'admin'];
  const buttonsObj: Record<string, ModuleDef> = {
    cad: { key: 'cad', label: 'DISPATCH' },
    mdt: { key: 'mdt', label: 'MOBILE' },
    admin: { key: 'admin', label: 'ADMIN' },
  };

  const availableModules: string[] = w.getAvailableModules();
  const modules: ModuleDef[] = buttonsOrder
    .filter((b) => w.isInArray(availableModules, b))
    .map((b) => buttonsObj[b]);

  return <SelectModuleScreen modules={modules} />;
}

/**
 * Unmount the SelectModuleScreen and replace its container with an empty
 * placeholder div so it can be re-initialised later.
 * Replaces the Vue 2 `destroySelectModuleScreen()` function.
 */
function destroySelectModuleScreen(): void {
  if (selectModuleContainer) {
    destroyReactComponent(selectModuleContainer);

    if (selectModuleContainer.parentNode) {
      const placeholder = document.createElement('div');
      placeholder.id = 'selectModuleScreen';
      selectModuleContainer.parentNode.replaceChild(
        placeholder,
        selectModuleContainer
      );
    }

    selectModuleContainer = null;
  }
}

/**
 * Mount the SelectModuleScreen React component into the
 * `#selectModuleScreen` element.
 * Replaces the Vue 2 `initSelectModuleScreen()` function.
 */
function initSelectModuleScreen(): void {
  destroySelectModuleScreen();

  const el = document.getElementById('selectModuleScreen');
  if (!el) return;

  selectModuleContainer = el;

  renderReactComponent(SelectModuleScreenWrapper, {}, selectModuleContainer);
}

// ── Window registration ───────────────────────────────────────────────

(window as any).initSelectModuleScreen = initSelectModuleScreen;
(window as any).destroySelectModuleScreen = destroySelectModuleScreen;

// ── Exports ───────────────────────────────────────────────────────────

export {
  initSelectModuleScreen,
  destroySelectModuleScreen,
};
